import React, { useEffect, useMemo, useState } from "react";
import styles from "./FloatingLabelSelect.module.css";
import { FloatingLabelSelectOption } from "../../../../Types/Form/FloatingLabelSelectOption";

type FloatingLabelSelectProps = {
  label: string;
  options: FloatingLabelSelectOption[];
  required?: boolean; // standard HTML required flag (kept)
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  value: string | undefined;
  requiredFooter?: string;
  noOptionsMessage?: string;
  openMessage?: string;
};

const FloatingLabelSelect: React.FC<FloatingLabelSelectProps> = ({
  label,
  options,
  required = false,
  disabled = false,
  onChange,
  value,
  requiredFooter,
  noOptionsMessage,
  openMessage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const requiredOptions = useMemo(
    () => options.filter(o => o.required),
    [options]
  );
  const hasAnyRequired = requiredOptions.length > 0;

  // Is current value one of the required options?
  const isCurrentValueRequired = useMemo(() => {
    if (!value) return false;
    const opt = options.find(o => o.value === value);
    return !!opt?.required;
  }, [options, value]);

  // Auto-heal to a required option if needed
  useEffect(() => {
    if (!hasAnyRequired) return;

    // If parent hasn't provided onChange, we can't enforce the invariant
    if (!onChange) {
      if (!isCurrentValueRequired) {
        // eslint-disable-next-line no-console
        console.warn(
          "FloatingLabelSelect: value must be one of the required options, but onChange is missing. Provide onChange to keep the value in sync."
        );
      }
      return;
    }

    if (!isCurrentValueRequired) {
      const firstRequired = requiredOptions[0];
      if (firstRequired) {
        const event = {
          target: { value: firstRequired.value },
        } as unknown as React.ChangeEvent<HTMLSelectElement>;
        onChange(event);
      }
    }
  }, [hasAnyRequired, isCurrentValueRequired, requiredOptions, onChange]);

  // When required options exist, only show those in the dropdown.
  const optionsToRender = hasAnyRequired ? requiredOptions : options;

  return (
    <div className={`${styles.floatingLabelSelect}`}>
      <div className={styles.labelRow}>
        <label className={styles.floatingLabel}>
          {label}
          {(required || hasAnyRequired) && <span className={styles.asterisk}>*</span>}
        </label>
        {openMessage && isOpen && (
          <span className={styles.openMessageInline} title={openMessage}>
            {openMessage}
          </span>
        )}
      </div>

      <select
        className={`${styles.select}`}
        value={value ?? ""} // keep controlled
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onChange={(e) => {
          if (!onChange) return;
          if (hasAnyRequired) {
            // Guard against programmatic value changes to non-required
            const next = options.find(o => o.value === e.target.value);
            if (next && !next.required) {
              // Ignore – invariant: must be one of required
              return;
            }
          }
          onChange(e);
        }}
        required={required || hasAnyRequired} // form-level required if any required options exist
        disabled={disabled}
        aria-required={required || hasAnyRequired}
        aria-invalid={hasAnyRequired? !isCurrentValueRequired : false}
      >
        {/* Placeholder is only permitted when no required options exist */}
        {!hasAnyRequired && (
          <option value="" disabled hidden>
            {options.length > 0 ? "Select an option" : noOptionsMessage || "No options available"}
          </option>
        )}

        {optionsToRender.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/*(hasAnyRequired || required) && 
      <label className={styles.footer}>
        {(requiredFooter || required) && "This is a required field"}
      </label>*/}
    </div>
  );
};

export default FloatingLabelSelect;
